import config from './index';
import { getErrorMessage } from './adminOrderFlowService';

export { getErrorMessage };

function headers() {
  const token = localStorage.getItem('token');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function parseBody(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

/** `/api/v1/admin/order-flow/orders/:orderId` — session routes live under `/sessions`. */
function adminOrderFlowOrderBase(orderId) {
  return `${config.apiUrl}/api/v1/admin/order-flow/orders/${encodeURIComponent(orderId)}`;
}

async function sessionsRequest(orderId, pathSuffix, options = {}) {
  const res = await fetch(`${adminOrderFlowOrderBase(orderId)}/sessions${pathSuffix}`, {
    ...options,
    headers: { ...headers(), ...options.headers },
  });
  const body = await parseBody(res);
  return { ok: res.ok, status: res.status, body };
}

/** Accept common API list wrappers */
export function unwrapSessionFlowList(body, pluralKeys = []) {
  if (Array.isArray(body)) return body;
  const d = body?.data ?? body?.result ?? body;
  if (Array.isArray(d)) return d;
  for (const k of pluralKeys) {
    const v = d?.[k];
    if (Array.isArray(v)) return v;
  }
  return [];
}

/** `GET …/order-flow/orders/:orderId/sessions` */
export function getSessionFlowSessions(orderId) {
  return sessionsRequest(orderId, '', { method: 'GET' });
}

/** `POST …/sessions/schedule` */
export function postSessionFlowSchedule(orderId, payload = {}) {
  return sessionsRequest(orderId, '/schedule', {
    method: 'POST',
    body: JSON.stringify(payload != null && typeof payload === 'object' ? payload : {}),
  });
}

/** `POST …/sessions/:sessionId/on-the-way` */
export function postSessionOnTheWay(orderId, sessionId, payload = {}) {
  const sid = encodeURIComponent(sessionId);
  return sessionsRequest(orderId, `/${sid}/on-the-way`, {
    method: 'POST',
    body: JSON.stringify(payload != null && typeof payload === 'object' ? payload : {}),
  });
}

/** `POST …/sessions/:sessionId/start` — body typically `{}` */
export function postSessionStart(orderId, sessionId, payload = {}) {
  const sid = encodeURIComponent(sessionId);
  return sessionsRequest(orderId, `/${sid}/start`, {
    method: 'POST',
    body: JSON.stringify(payload != null && typeof payload === 'object' ? payload : {}),
  });
}

/** `POST …/sessions/:sessionId/complete-job` */
export function postSessionCompleteJob(orderId, sessionId, payload = {}) {
  const sid = encodeURIComponent(sessionId);
  return sessionsRequest(orderId, `/${sid}/complete-job`, {
    method: 'POST',
    body: JSON.stringify(payload != null && typeof payload === 'object' ? payload : {}),
  });
}

/** `POST …/sessions/:sessionId/pause` — `{ scheduled_resume_at }` */
export function postSessionPause(orderId, sessionId, payload = {}) {
  const sid = encodeURIComponent(sessionId);
  return sessionsRequest(orderId, `/${sid}/pause`, {
    method: 'POST',
    body: JSON.stringify(payload != null && typeof payload === 'object' ? payload : {}),
  });
}

/** `POST …/sessions/:sessionId/done-for-day` */
export function postSessionDoneForDay(orderId, sessionId, payload = {}) {
  const sid = encodeURIComponent(sessionId);
  return sessionsRequest(orderId, `/${sid}/done-for-day`, {
    method: 'POST',
    body: JSON.stringify(payload != null && typeof payload === 'object' ? payload : {}),
  });
}

/** `POST …/sessions/:sessionId/resume` */
export function postSessionResume(orderId, sessionId) {
  const sid = encodeURIComponent(sessionId);
  return sessionsRequest(orderId, `/${sid}/resume`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/**
 * `POST …/sessions/:sessionId/complete-inspection`
 * Body: `{ outcome: 'start_job_now' }` | `{ outcome: 'schedule_job', scheduled_visit_at }` | `{ outcome: 'another_inspection', scheduled_visit_at }`
 */
export function postSessionCompleteInspection(orderId, sessionId, payload = {}) {
  const sid = encodeURIComponent(sessionId);
  return sessionsRequest(orderId, `/${sid}/complete-inspection`, {
    method: 'POST',
    body: JSON.stringify(payload != null && typeof payload === 'object' ? payload : {}),
  });
}
